import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalController, PopoverController } from '@ionic/angular';
import { RestrictionsPopoverComponent } from './restrictions-popover/restrictions-popover.component';
import { ToastService } from '../../services/toast/toast.service';
import { LoadingService } from '../loading/loading.service';
import { GroupPermissionsService } from '../../services/group-permissions/group-permissons.service';

@Component({
  selector: 'app-permission-modal',
  templateUrl: './permission-modal.component.html',
  styleUrls: ['./permission-modal.component.scss'],
})
export class PermissionModalComponent implements OnInit {
  dropdownStates: { [key: string]: boolean } = {};
  @Input() permissions: any[] = [];
  menus: any[] = [];
  permissionsData: any[] = [];
  form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private modalCtrl: ModalController,
    private popoverController: PopoverController,
    private loadingService: LoadingService,
    private groupPermissionsService: GroupPermissionsService,
    private toastService: ToastService
  ) {
    this.form = this.fb.group({});
    this.dropdownStates['gestaoDeUsuarios'] = false;
  }

  ngOnInit() {
    this.loadMenus();
  }

  async loadMenus() {
    this.loadingService.showLoading();
    try {
      const response = await this.groupPermissionsService
        .getMenus()
        .toPromise();
      this.menus = (response as any[]) || [];
      this.initializeFormControls();
      this.setPermissionsFromInput();
    } catch (error) {
      console.error(error);
    } finally {
      this.loadingService.hideLoading();
    }
  }

  initializeFormControls() {
    const formControls = this.menus.reduce((controls, menu) => {
      const sanitizedMenuName = this.sanitizeControlName(menu.nome);
      controls[sanitizedMenuName] = [false, Validators.required];
      menu.subFuncionalidades.forEach((subMenu: any) => {
        const sanitizedSubMenuName = this.sanitizeControlName(subMenu.nome);
        controls[sanitizedSubMenuName] = [false, Validators.required];
        subMenu.subFuncionalidades.forEach((subSubMenu: any) => {
          const sanitizedSubSubMenuName = this.sanitizeControlName(
            subSubMenu.nome
          );
          controls[sanitizedSubSubMenuName] = [false, Validators.required];
        });
      });
      return controls;
    }, {});

    this.form = this.fb.group(formControls);
  }

  sanitizeControlName(name: string): string {
    return name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  setPermissionsFromInput() {
    this.permissions.forEach((permission: any) => {
      const processFuncionalidade = (funcionalidade: any) => {
        const isEditFormat = funcionalidade !== undefined;

        if (isEditFormat) {
          const parentControlName = this.sanitizeControlName(funcionalidade.nome);
          const parentControl = this.form.get(parentControlName);
          if (parentControl) {
            parentControl.setValue(true);
          }

          const existingPermissionIndex = this.permissionsData.findIndex(
            (perm) => perm.idMenu === funcionalidade.id
          );

          if (existingPermissionIndex !== -1) {
            this.permissionsData[existingPermissionIndex].restrictions =
              funcionalidade.restricoes || [];
          } else {
            this.permissionsData.push({
              idMenu: funcionalidade.id,
              nome: funcionalidade.nome,
              restrictions: funcionalidade.restricoes || [],
            });
          }

          funcionalidade.restricoes?.forEach((restriction: any) => {
            if (restriction.nome) {
              const restrictionControlName = this.sanitizeControlName(restriction.nome);
              const restrictionControl = this.form.get(restrictionControlName);
              if (restrictionControl) {
                restrictionControl.setValue(true);
              }
            }
          });

          funcionalidade.subFuncionalidades?.forEach((subFunc: any) => {
            processFuncionalidade(subFunc);
          });
        }
      };

      if (permission.funcionalidade) {
        processFuncionalidade(permission.funcionalidade);

        permission.funcionalidade.subFuncionalidades.forEach((subFunc: any) => {
          processFuncionalidade(subFunc);
        });
      }

      else {
        const parentControlName = this.sanitizeControlName(permission.nome);
        const parentControl = this.form.get(parentControlName);
        if (parentControl) {
          parentControl.setValue(true);
        }

        const existingPermissionIndex = this.permissionsData.findIndex(
          (perm) => perm.idMenu === permission.idMenu
        );

        if (existingPermissionIndex !== -1) {
          this.permissionsData[existingPermissionIndex].restrictions =
            permission.restrictions || [];
        } else {
          this.permissionsData.push({
            idMenu: permission.idMenu,
            nome: permission.nome,
            restrictions: permission.restrictions || [],
          });
        }

        permission.restrictions?.forEach((restriction: any) => {
          if (restriction.nome) {
            const restrictionControlName = this.sanitizeControlName(restriction.nome);
            const restrictionControl = this.form.get(restrictionControlName);
            if (restrictionControl) {
              restrictionControl.setValue(true);
            }
          }
        });
      }
    });
  }

  onCheckboxChange(controlPath: string) {
    const control = this.form.get(controlPath);
    if (control) {
      const isChecked = control.value;
      control.setValue(isChecked);

      this.updateChildCheckboxes(controlPath, isChecked);

      const parentMenuName = this.getParentMenuName(controlPath);
      if (parentMenuName) {
        this.updateParentCheckboxState(parentMenuName);
      }
    }
  }

  updateChildCheckboxes(controlPath: string, isChecked: boolean) {
    for (const menu of this.menus) {
      const sanitizedMenuName = this.sanitizeControlName(menu.nome);
      if (sanitizedMenuName === controlPath) {
        menu.subFuncionalidades.forEach(
          (subMenu: { nome: string; subFuncionalidades: any[] }) => {
            const sanitizedSubMenuName = this.sanitizeControlName(subMenu.nome);
            this.form.get(sanitizedSubMenuName)?.setValue(isChecked);

            subMenu.subFuncionalidades.forEach(
              (subSubMenu: { nome: string }) => {
                const sanitizedSubSubMenuName = this.sanitizeControlName(
                  subSubMenu.nome
                );
                this.form.get(sanitizedSubSubMenuName)?.setValue(isChecked);
              }
            );
          }
        );
      } else {
        menu.subFuncionalidades.forEach(
          (subMenu: { subFuncionalidades: any[]; nome: string }) => {
            subMenu.subFuncionalidades.forEach(
              (subSubMenu: { nome: string }) => {
                const sanitizedSubSubMenuName = this.sanitizeControlName(
                  subSubMenu.nome
                );
                if (sanitizedSubSubMenuName === controlPath) {
                  const sanitizedParentMenuName = this.sanitizeControlName(
                    subMenu.nome
                  );
                  this.form.get(sanitizedParentMenuName)?.setValue(isChecked);
                }
              }
            );
          }
        );
      }
    }
  }

  getParentMenuName(controlPath: string): string | null {
    for (const menu of this.menus) {
      for (const subMenu of menu.subFuncionalidades) {
        for (const subSubMenu of subMenu.subFuncionalidades) {
          const sanitizedSubSubMenuName = this.sanitizeControlName(
            subSubMenu.nome
          );
          if (sanitizedSubSubMenuName === controlPath) {
            return subMenu.nome;
          }
        }
      }
    }
    return null;
  }

  updateParentCheckboxState(parentMenuName: string) {
    const parentControl = this.form.get(
      this.sanitizeControlName(parentMenuName)
    );
    if (parentControl) {
      const hasCheckedChildren = this.menus.some((menu) => {
        return menu.subFuncionalidades.some(
          (subMenu: { subFuncionalidades: any[] }) => {
            return subMenu.subFuncionalidades.some((subSubMenu) => {
              const subSubControl = this.form.get(
                this.sanitizeControlName(subSubMenu.nome)
              );
              return subSubControl ? subSubControl.value : false;
            });
          }
        );
      });

      parentControl.setValue(hasCheckedChildren);
    }
  }

  toggleDropdown(dropdown: string) {
    this.dropdownStates[dropdown] = !this.dropdownStates[dropdown];
  }

  toggleDropdownOnKeydown(event: KeyboardEvent, dropdown: string) {
    if (['Space', 'Enter'].includes(event.key)) {
      event.preventDefault();
      this.toggleDropdown(dropdown);
    }
  }

  onCheckboxKeydown(event: KeyboardEvent, controlPath: string) {
    if (['Space', 'Enter'].includes(event.key)) {
      event.preventDefault();
      const checkbox = event.target as HTMLIonCheckboxElement;
      const isChecked = !checkbox.checked;
      this.form.get(controlPath)?.setValue(isChecked);
    }
  }

  async presentPopover(ev: any, id: string, nome: string) {
    const currentRestrictions =
      this.permissionsData.find((permission) => permission.idMenu === id)
        ?.restrictions || [];

    const popover = await this.popoverController.create({
      component: RestrictionsPopoverComponent,
      componentProps: { currentRestrictions },
      event: ev,
      translucent: true,
    });

    popover.onWillDismiss().then((data) => {
      if (data.data) {
        this.handleRestrictionsData(data.data, id, nome);
      }
    });

    await popover.present();
  }

  handleRestrictionsData(data: any, idMenu: string, nome: string) {
    const existingPermissionIndex = this.permissionsData.findIndex(
      (permission) => permission.idMenu === idMenu
    );

    if (existingPermissionIndex !== -1) {
      this.permissionsData[existingPermissionIndex].restrictions = data;
    } else {
      this.permissionsData.push({
        idMenu,
        nome,
        restrictions: data,
      });
    }

    const control = this.form.get(this.sanitizeControlName(nome));
    if (control) {
      control.setValue(true);
    }
  }

  presentPopoverKeydown(event: KeyboardEvent) {
    if (['Space', 'Enter'].includes(event.key)) {
      event.preventDefault();
    }
  }

  cancel() {
    return this.modalCtrl.dismiss(null, 'cancel');
  }

  onSubmit() {
    const permissions: { idMenu: any; nome: any; restrictions: any }[] = [];

    this.menus.forEach((menu) => {
      const sanitizedMenuName = this.sanitizeControlName(menu.nome);

      if (this.form.get(sanitizedMenuName)?.value) {
        permissions.push({
          idMenu: menu.id,
          nome: menu.nome,
          restrictions:
            this.permissionsData.find((perm) => perm.idMenu === menu.id)
              ?.restrictions || [],
        });
      }

      menu.subFuncionalidades.forEach(
        (subMenu: { nome: string; id: any; subFuncionalidades: any[] }) => {
          const sanitizedSubMenuName = this.sanitizeControlName(subMenu.nome);

          if (this.form.get(sanitizedSubMenuName)?.value) {
            permissions.push({
              idMenu: subMenu.id,
              nome: subMenu.nome,
              restrictions:
                this.permissionsData.find((perm) => perm.idMenu === subMenu.id)
                  ?.restrictions || [],
            });
          }

          subMenu.subFuncionalidades.forEach((subSubMenu) => {
            const sanitizedSubSubMenuName = this.sanitizeControlName(
              subSubMenu.nome
            );

            if (this.form.get(sanitizedSubSubMenuName)?.value) {
              permissions.push({
                idMenu: subSubMenu.id,
                nome: subSubMenu.nome,
                restrictions:
                  this.permissionsData.find(
                    (perm) => perm.idMenu === subSubMenu.id
                  )?.restrictions || [],
              });
            }
          });
        }
      );
    });

    if (permissions.length === 0) {
      this.toastService.showError(
        'Por favor, preencha as permissões para continuar.'
      );
      return;
    }

    return this.modalCtrl.dismiss(permissions, 'confirm');
  }

  setAdminProfile() {
    this.menus.forEach((menu) => {
      const control = this.form.get(this.sanitizeControlName(menu.nome));
      if (control) {
        control.setValue(true);
      }
      this.updatePermissionsData(menu.id, menu.nome, []);

      menu.subFuncionalidades.forEach(
        (subMenu: { nome: string; id: string; subFuncionalidades: any[] }) => {
          const subControl = this.form.get(
            this.sanitizeControlName(subMenu.nome)
          );
          if (subControl) {
            subControl.setValue(true);
          }
          this.updatePermissionsData(subMenu.id, subMenu.nome, []);

          subMenu.subFuncionalidades.forEach(
            (subSubMenu: { nome: string; id: string }) => {
              const subSubControl = this.form.get(
                this.sanitizeControlName(subSubMenu.nome)
              );
              if (subSubControl) {
                subSubControl.setValue(true);
              }
              this.updatePermissionsData(subSubMenu.id, subSubMenu.nome, []);
            }
          );
        }
      );
    });
  }

  updatePermissionsData(idMenu: string, nome: string, restrictions: any[]) {
    const existingPermissionIndex = this.permissionsData.findIndex(
      (perm) => perm.idMenu === idMenu
    );

    if (existingPermissionIndex !== -1) {
      this.permissionsData[existingPermissionIndex].restrictions = restrictions;
    } else {
      this.permissionsData.push({
        idMenu,
        nome,
        restrictions,
      });
    }
  }
}